import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'

import { httpOk, GET, POST, PUT } from 'helpers/api'
import { formatMoney, formatMoneyInput, notify } from 'helpers/helpers'
import AwardBasisField from './AwardBasisField'
import { ruleAwardType, ruleAwardValue, setRuleAwardValue, setRuleAwardType, isRuleAwardGrowing, formatRuleAward, isAwardAmountAllowed, hasAmountRules } from './ruleAward'

function CreateUpdate() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { id } = useParams()

	const rulesExample = [
		{
			'from_amount': 0,
			'award_percentage': 1,
		},
		{
			'from_amount': 100000,
			'award_percentage': 2,
		},
		{
			'from_amount': 500000,
			'award_percentage': 3,
		},
		{
			'from_amount': 1000000,
			'award_percentage': 4,
		}
	]

	const [data, setData] = useState({
		"name": "",
		"loyalty_service_type_id": 1,
		"audience": 'guests',
		"award_basis": 'sales',
		"poses": [],
		"rules": [],
	})
	const emptyRule = { 'award_percentage': '', 'award_amount': '', 'award_type': 'percent', 'from_amount': '' }
	const [rule, setRule] = useState(emptyRule)
	const [poses, setPoses] = useState([])
	const [orderDeliveryTypes, setOrderDeliveryTypes] = useState([])

	async function createUpdate() {
		var dataCopy = { ...data }
		var response

		if (rule.from_amount && ruleAwardValue(rule)) {
			if (!checkRule(dataCopy.rules, rule)) {
				notify(t('error'))
				return
			}

			dataCopy.rules = [...dataCopy.rules, { ...rule }]
			setData(dataCopy)
			setRule(emptyRule)
		}

		if (dataCopy.rules.length === 0) {
			notify(t('loyalty_rules_required'))
			return
		}

		if (!dataCopy.id) {
			response = await POST('/loyalty/services-amount', dataCopy, { loader: true })
		} else {
			response = await PUT('/loyalty/services-amount', dataCopy, { loader: true })
		}

		if (httpOk(response)) navigate(-1)
	}

	function checkRule(rules, ruleCopy) {
		if (rules.length === 0) return true

		var lastRule = rules[rules.length - 1]

		if (Number(lastRule['from_amount']) >= Number(ruleCopy.from_amount)) return false
		if (!isRuleAwardGrowing(lastRule, ruleCopy)) return false

		return true
	}

	function addRule() {
		var dataCopy = { ...data }
		var ruleCopy = { ...rule }

		if (!checkRule(dataCopy.rules, ruleCopy)) {
			notify(t('error'))
			return
		}

		dataCopy.rules.push(ruleCopy)
		setData(dataCopy)
		setRule(emptyRule)
	}

	function deleteRule(index) {
		var dataCopy = { ...data }
		dataCopy.rules.splice(index, 1)
		setData(dataCopy)
	}

	async function getOrderDeliveryTypes() {
		const response = await GET('/helper/order-type-subs')
		if (httpOk(response)) setOrderDeliveryTypes(response.data)
	}

	async function getData() {
		const response = await GET('/helper/poses', {}, { loader: true })
		if (httpOk(response)) setPoses(response.data)
		if (id) {
			const response = await GET(`/loyalty/services-amount/${id}`, {}, { loader: true })
			if (httpOk(response)) setData(response.data)
		}
	}

	useEffect(() => {
		if (!isAwardAmountAllowed(data) && ruleAwardType(rule) === 'amount') setRule(setRuleAwardType(rule, 'percent'))
	}, [data.audience, data.award_basis]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		getData()
		getOrderDeliveryTypes()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex">
				<div className="d-flex text-truncate">
					<h4 className="vertical-center">
						{t('loyalty')}
					</h4>
					<div className="vertical-center mt-1">
						<i className="uil uil-angle-double-right fz-20"></i>
					</div>
					<div className="vertical-center mt-1">
						<h6>{t('create')}</h6>
					</div>
				</div>
			</div>

			<div className="card mb-3">
				<div className="card-body">

					<div className="row">
						<div className="col-md-4 border-right">
							<div className="form-group">
								<label>{t('name2')}</label>
								<input type="text" className="form-control" name="name" autoComplete="off"
									value={data.name ?? ''}
									onChange={(e) => setData({ ...data, 'name': e.target.value })} />
							</div>
							{/* Аудиторию выбирают при создании: у программы для организаций свои ступени,
								гости по ней баллов не получают */}
							<div className="form-group">
								<div className="d-flex justify-content-between">
									<p className="fw-600 vertical-center mb-0">{t('org_program_switch')}</p>
									<div className="form-check form-switch form-switch-md">
										<input type="checkbox" className="form-check-input" name="audience"
											disabled={!!data.id}
											checked={data.audience === 'organizations'}
											onChange={(e) => setData({ ...data, 'audience': e.target.checked ? 'organizations' : 'guests' })} />
									</div>
								</div>
								<small className="text-muted">{t('org_program_switch_hint')}</small>
							</div>
							<AwardBasisField data={data} setData={setData} />
							{data.audience === 'organizations' && data.award_basis === 'payments' && hasAmountRules(data.rules) &&
								<div className="alert alert-warning">{t('award_amount_payments_conflict')}</div>
							}

							<div className="form-group w-100">
								<label>{t('order_type')}<span className="required-mark">*</span></label>
								<Select
									isMulti
									options={orderDeliveryTypes}
									value={data.order_type_subs}
									onChange={(options) => setData({ ...data, 'order_type_subs': options })}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => option.name}
									getOptionValue={(option) => option.id}
								/>
							</div>

							<div className="form-group">
								<label>{t('poses')}<span className="required-mark">*</span></label>
								<Select
									isMulti
									value={data.poses}
									options={poses}
									onChange={(options) => setData({ ...data, 'poses': options })}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => option.name}
									getOptionValue={(option) => option.id}
								/>
							</div>

							<h5 className="mt-5">{t('example')}</h5>
							<div className="table-responsive">
								<table className="table table-bordered mb-0">
									<thead>
										<tr>
											<th>{t('amount')}</th>
											<th>{t('bonus')} %</th>
										</tr>
									</thead>
									<tbody>
										{rulesExample.map((item, index) => (
											<tr key={index}>
												<td>
													<span>{formatMoney(item.from_amount)}</span>
												</td>
												<td>
													<span>{item.award_percentage}%</span>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

						</div>
						<div className="col-md-4">
							<div className="table-responsive">
								<table className="table table-bordered mb-0">
									<thead>
										<tr>
											<th>{t('amount')}</th>
											<th>{t('bonus')}{data.audience === 'organizations' ? '' : ' %'}</th>
											<th></th>
										</tr>
									</thead>
									<tbody>
										<tr>
											<td>
												<input type="text" pattern="[0-9]*" className="form-control"
													value={formatMoneyInput(rule.from_amount ?? '') ?? ''}
													onChange={(e) => setRule({ ...rule, 'from_amount': e.target.value.replace(/[^0-9.]/g, '') })} />
											</td>
											<td>
												{/* Программа для организаций: процент или фиксированная сумма с чека */}
												<div className="d-flex gap-1">
													<input type="text" pattern="[0-9]*" className="form-control"
														value={ruleAwardType(rule) === 'amount' ? formatMoneyInput(ruleAwardValue(rule)) : ruleAwardValue(rule)}
														onChange={(e) => setRule(setRuleAwardValue(rule, e.target.value.replace(/[^0-9.]/g, '')))} />
													{isAwardAmountAllowed(data) &&
														<select className="form-select" style={{ 'width': 'auto' }}
															value={ruleAwardType(rule)}
															onChange={(e) => setRule(setRuleAwardType(rule, e.target.value))}>
															<option value="percent">%</option>
															<option value="amount">{t('org_award_per_cheque')}</option>
														</select>
													}
												</div>
											</td>
											<td>
												<button className="btn btn-primary" onClick={() => addRule()}
													disabled={!ruleAwardValue(rule) || !rule.from_amount}>
													{t('add')}
												</button>
											</td>
										</tr>
										{data.rules.map((item, index) => (
											<tr key={index}>
												<td>{formatMoney(item.from_amount)}</td>
												<td>{formatRuleAward(item, t)}</td>
												<td>
													<div className="d-flex justify-content-center">
														<div className="d-flex justify-content-center">
															<div className="table-action-button table-action-danger-button"
																onClick={() => deleteRule(index)}>
																<i className="uil-times"></i>
															</div>
														</div>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					</div>

					<div className="btn-wrapper">
						<Link className="btn btn-warning btn-rounded btn-wide me-2" to="/loyalty/services" tabIndex="-1">
							{t('cancel')}
						</Link>
						<button type="button" className="btn btn-primary btn-rounded btn-wide" tabIndex="-1"
							onClick={() => createUpdate()}>
							{t('save')}
						</button>
					</div>

				</div>
			</div>
		</>
	)
}

export default CreateUpdate